import React, { useCallback, useEffect, useState } from 'react';
import {
  entityStore,
  sirenActions,
  useSirenActionFields,
  useSirenEntity,
} from '~/utility/BinSentry-ui-utility';
import BnLoadingButton from '~/common/bn-loading-button';
import { useFileDownloader } from '~/common/download-file';
import { DateTime } from 'luxon';
import EaiExportDialog from '~/orders/integrations/eai/eai-export-dialog';
import parseSiren from 'siren-parser';
import { FileDocumentArrowRight } from '~/common/mdi-icons';

export function OrderDownloadEaiXmlButton({ orderHref, children }) {
  const { entity: orderEntity } = useSirenEntity({ href: orderHref });
  const integrationsHref = orderEntity?.getLinkByRel('https://api.binsentry.com/rel/integrations')?.href;
  const { entity: integrationsEntity } = useSirenEntity({ href: integrationsHref });
  const eaiHref = integrationsEntity?.getSubEntityByRel('https://api.binsentry.com/rel/eai-integration')?.href;
  const { entity: eaiEntity } = useSirenEntity({ href: eaiHref });
  const { sirenAction } = useSirenActionFields({ entity: eaiEntity, actionName: 'export-order' });
  const downloadFile = useFileDownloader();

  const [ loading, setLoading ] = useState(false);
  const [dialogIntegrationHref, setDialogIntegrationHref] = useState(undefined);

  // we need to re-validate
  const refreshEaiEntity = useCallback(() => {
    if (eaiHref) {
      entityStore.fetchWait(eaiHref);
    }
  }, [eaiHref]);

  useEffect(function refreshEaiEntityWhenOrderChanges() {
    refreshEaiEntity();
  }, [orderEntity, refreshEaiEntity]);

  const submit = useCallback(
    function doSubmit() {
      async function downloadOrLaunchDialog() {
        if (!eaiEntity) {
          return;
        }

        if (!eaiEntity.properties.valid || eaiEntity.properties.lastExportedAt) {
          // show the full dialog if there's anything important for the user to see. We could tailor the API to serve
          // this case better, but realistically we should probably just have a separate dialog that can be backed by
          // the entity we already have, rather than working with the search-based integration endpoint. This is just a
          // quick solution for an edge case.
          const { entity: bin } = await entityStore.fetchWait(orderEntity.getSubEntityByRel('https://api.binsentry.com/rel/bin').href);
          const { entity: barn } = await entityStore.fetchWait(parseSiren(bin).getSubEntityByRel('https://api.binsentry.com/rel/owner').href);
          const { entity: mill } = await entityStore.fetchWait(parseSiren(barn).getLinkByRel('https://api.binsentry.com/rel/account').href);
          const { entity: ordersSearch } = await entityStore.fetchWait(parseSiren(mill).getLinkByRel('https://api.binsentry.com/rel/search-orders').href);
          const { url: selectionUrl } = sirenActions.parseAction(parseSiren(ordersSearch).getActionByName('select'), { orderIds: [orderHref], limit: 1 });
          const { entity: ordersSelection } = await entityStore.fetchWait(selectionUrl);
          const { entity: integrations } = await entityStore.fetchWait(parseSiren(ordersSelection).getLinkByRel('https://api.binsentry.com/rel/integrations').href);
          const eaiIntegrationHref = parseSiren(integrations)?.getSubEntityByRel('https://api.binsentry.com/rel/eai-integration')?.href;
          setDialogIntegrationHref(eaiIntegrationHref);
          return;
        }

        const { entity: response } = await sirenActions.submitAction(sirenAction);
        const url = `data:text/plain;charset=UTF-8,${encodeURIComponent(response.properties.xml)}`;
        const fileName = `feedofficepro-export-${DateTime.now().toISODate()}.xml`;
        await downloadFile({ url, fileName });
        return refreshEaiEntity();
      }

      setLoading(true);
      downloadOrLaunchDialog().finally(() => setLoading(false));
    },
    [downloadFile, orderEntity, eaiEntity, sirenAction, orderHref, refreshEaiEntity],
  );

  if (!eaiEntity) {
    return null;
  }

  return <>
    <EaiExportDialog eaiIntegrationHref={dialogIntegrationHref} open={!!dialogIntegrationHref} handleClose={() => {
      setDialogIntegrationHref(undefined);
      refreshEaiEntity();
    }}/>
    <BnLoadingButton
      loading={loading}
      startIcon={<FileDocumentArrowRight />}
      variant='outlined'
      onClick={submit}
    >
      {children || 'Export for EAI'}
    </BnLoadingButton>
  </>;
}
